///////////////////////////////////////////////////////////////
//                                                           //
//  Program: IlsExport.jsx                                   //
//  Application: Settings                                    //
//  Option: ILS Export — MITS 5.0 feed for Yardi RentCafe   //
//  Developer: Orion Haus Dev                                //
//  Date: 2026-07-10                                         //
//                                                           //
///////////////////////////////////////////////////////////////

import React, { Component } from "react";
import { Container } from "react-bootstrap";
import SettingsSidebar from "../common/SettingsSidebar";
import IlsExportService from "../../services/IlsExportService";
import Alerts from "../common/Alerts";

class IlsExport extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pathname: window.location.pathname,
      property_slug: this.props.match.params.propertySlug,
      isLoading: false,
      isPageLoading: true,
      ilsSettings: null,
      propertyName: "",
      showAlertModal: false,
      alertModalType: "",
      alertModalTitle: "",
      alertModalMessage: "",
    };

    this.handleRegenerate = this.handleRegenerate.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  componentDidMount() {
    this.loadSettings();
  }

  closeModal() {
    this.setState({ showAlertModal: false });
  }

  /**
   * Load ILS settings and last-generated status for this property.
   */
  async loadSettings() {
    this.setState({ isPageLoading: true });

    try {
      let res = await IlsExportService.getSettings(this.state.property_slug);

      if (global.successStatus.includes(res.status)) {
        let result = res.data && res.data.result ? res.data.result : {};
        this.setState({
          propertyName: result.property_name || "",
          ilsSettings: result.ils_settings || null,
          isPageLoading: false,
        });
      } else {
        let message =
          res.data && res.data.message
            ? res.data.message
            : "Unable to load ILS settings.";
        this.setState({
          showAlertModal: true,
          alertModalType: "error",
          alertModalTitle: "Error",
          alertModalMessage: message,
          isPageLoading: false,
        });
      }
    } catch (err) {
      this.setState({
        showAlertModal: true,
        alertModalType: "error",
        alertModalTitle: "Error",
        alertModalMessage: "An error occurred while loading ILS settings.",
        isPageLoading: false,
      });
    }
  }

  /**
   * Trigger MITS 5.0 feed regeneration on the backend.
   * Generates, validates, and stores the XML file on the server.
   */
  async handleRegenerate() {
    if (!global.userPermissions.checkPermission("ils-feed-update")) {
      this.setState({
        showAlertModal: true,
        alertModalType: "error",
        alertModalTitle: "Permission Denied",
        alertModalMessage: "You do not have permission to regenerate the ILS feed.",
      });
      return;
    }

    this.setState({
      isLoading: true,
      showAlertModal: false,
    });

    try {
      let res = await IlsExportService.regenerateFeed(this.state.property_slug);

      if (global.successStatus.includes(res.status)) {
        this.setState({
          showAlertModal: true,
          alertModalType: "success",
          alertModalTitle: "Success",
          alertModalMessage: "MITS 5.0 feed generated and validated successfully.",
          isLoading: false,
        });
        this.loadSettings();
      } else {
        let message =
          res.data && res.data.message
            ? res.data.message
            : "Feed generation failed. Please check the server logs.";
        this.setState({
          showAlertModal: true,
          alertModalType: "error",
          alertModalTitle: "Error",
          alertModalMessage: message,
          isLoading: false,
        });
      }
    } catch (err) {
      this.setState({
        showAlertModal: true,
        alertModalType: "error",
        alertModalTitle: "Error",
        alertModalMessage: "An error occurred while regenerating the feed.",
        isLoading: false,
      });
    }
  }

  /**
   * Format a UTC timestamp string for display, or return "Never".
   */
  formatTimestamp(ts) {
    if (!ts) {
      return "Never";
    }
    let d = new Date(ts);
    if (isNaN(d.getTime())) {
      return ts;
    }
    return d.toLocaleString();
  }

  render() {
    let {
      property_slug,
      isLoading,
      isPageLoading,
      ilsSettings,
      showAlertModal,
      alertModalType,
      alertModalTitle,
      alertModalMessage,
    } = this.state;

    return (
      <main>
        <Container className="innter-container auto_list_sec">
          <div className="d-flex flex-column">
            <div className="row row-xs mg-t-0 pd-t-0 pd-lg-l-15 pd-xs-l-0">

              {/* Sidebar */}
              {property_slug !== undefined && property_slug !== "" && (
                <div className="col-md-2 left">
                  <div className="child-menu-section">
                    <aside>
                      <SettingsSidebar property_slug={property_slug} />
                    </aside>
                  </div>
                </div>
              )}

              {/* Main content */}
              <div className="col-md-10 right settingsidebarNav">
                <div className="col-md-12 mg-t-20 mg-b-10"></div>
                <div className="col-md-12 mg-t-0 mg-b-10 pd-t-0 pd-b-10 pd-l-15 pd-r-0">
                  <div className="dashbaord-pageHeadertitle d-flex justify-content-between align-items-center flex-wrap mg-b-0">
                    <h3>ILS Export</h3>
                  </div>
                </div>

                <div className="master">
                  <div className="table-responsive">
                    <div className="col-md-12 pl-0 pr-0">
                      <Alerts
                        show={showAlertModal}
                        type={alertModalType}
                        title={alertModalTitle}
                        message={alertModalMessage}
                      />
                    </div>

                    {isPageLoading ? (
                      <div className="text-center pd-t-30 pd-b-30">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <div className="col-md-12 pl-0 pr-0 pd-t-15">

                        {/* Feed Status card */}
                        <div className="row mg-b-20">
                          <div className="col-md-6">
                            <div className="card pd-20">
                              <h6 className="mg-b-15">Feed Status</h6>

                              {ilsSettings ? (
                                <table className="table table-sm mb-0">
                                  <tbody>
                                    <tr>
                                      <td className="text-muted">Platform</td>
                                      <td>{ilsSettings.platform || "—"}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">ILS Property ID</td>
                                      <td>
                                        {ilsSettings.ils_property_id || (
                                          <span className="text-muted">Not configured</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Enabled</td>
                                      <td>
                                        {ilsSettings.enabled === 1 ? (
                                          <span className="badge badge-success-outline">Yes</span>
                                        ) : (
                                          <span className="badge badge-secondary-outline">No</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Last Generated</td>
                                      <td>{this.formatTimestamp(ilsSettings.last_generated_at)}</td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">XSD Validation</td>
                                      <td>
                                        {ilsSettings.last_validated === 1 ? (
                                          <span className="badge badge-success-outline">Passed</span>
                                        ) : ilsSettings.last_generated_at ? (
                                          <span className="badge badge-danger-outline">Failed</span>
                                        ) : (
                                          <span className="text-muted">—</span>
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="text-muted">Output File</td>
                                      <td>{ilsSettings.output_path || "—"}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              ) : (
                                <p className="text-muted mb-0">No ILS settings found for this property.</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Regenerate action */}
                        {global.userPermissions.checkPermission("ils-feed-update") && (
                          <div className="col-md-12 pl-0 pr-0 pd-b-15">
                            <p className="text-muted mg-b-10">
                              Clicking Regenerate Feed will generate a new MITS 5.0 XML file,
                              validate it against the XSD schema, and save it to the server.
                              The file is not pushed to Yardi automatically at this stage.
                            </p>
                            <button
                              className="btn btn-success btn-block btn-with-icon"
                              onClick={this.handleRegenerate}
                              // disabled={isLoading || !ilsSettings}
                            >
                              {isLoading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm mg-r-5"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Generating...
                                </>
                              ) : (
                                "Regenerate Feed"
                              )}
                            </button>
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </Container>
      </main>
    );
  }
}

export default IlsExport;
